import React from 'react'
import { FormProvider } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { makeStyles, Typography } from 'transfix-ui/core'
import { NewDriverAutosuggest } from 'apps/fleet-planner/components/atoms/NewDriverAutosuggest'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { ILoadDetailsState } from 'apps/fleet-planner/types/loadDetails'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors/load'
import { IStore } from 'apps/fleet-planner/utils/types/store'
import { ShareLoadTooltip } from '../../../ShareLoadTooltip'
import { ShareLoadViaSmsModalForm } from './ShareLoadViaSmsModalForm'
import { TShareLoadViaSmsModalSchema } from './ShareLoadViaSmsModalSchema'
import { useShareLoadViaSmsModalForm } from './useShareLoadViaSmsModalForm'

interface IShareLoadViaSmsModalProps extends Omit<IModalBaseProps, 'modalType'> {} // The interface of the props of the react component "ShareLoadViaSmsModal"

const useShareLoadViaSmsModalStyles = makeStyles(({ spacing }) => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    flexGap: spacing(2),
    margin: `${spacing(2)} 0px`,
  },
  row: {
    display: 'flex',
    flexGap: spacing(2),
    alignItems: 'center',
  },
}))

export const ShareLoadViaSmsModal = ({
  onClose,
  open = true,
  ...props
}: IShareLoadViaSmsModalProps) => {
  const { trackEvent } = useFMSAnalytics()
  const methods = useShareLoadViaSmsModalForm({
    defaultValues: {
      oneTime: false,
    },
  })

  const { handleSubmit } = methods || {}

  const history = useHistory()
  const { pathname } = history.location
  const onSubmit = (data: TShareLoadViaSmsModalSchema) => {
    if (data.oneTime) {
      trackEvent('Driver Led Status', 'SELECTED_LOAD_ONE_TIME_UPDATE')
      return history.push(pathname.replace(/(\/[^/]+)$/, '/share'))
    }
    trackEvent('Driver Led Status', 'SELECTED_LOAD_UPDATE_THROUGHOUT')
    return history.push(pathname.replace(/(\/[^/]+)$/, '/sms-prompt-settings'))
  }
  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: onClose,
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Next',
      action: handleSubmit(onSubmit),
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  const classes = useShareLoadViaSmsModalStyles()
  const { load: shipment } = useSelector<IStore, ILoadDetailsState>(loadDetailsSelector)
  const { drivers } = shipment || {}
  const [driverOne] = drivers || []

  return (
    <ModalBase
      dataTest='ShareLoadViaSmsModal'
      modalType={MODAL_TYPES.form}
      size={6}
      open={open}
      actions={actions}
      bodyClassName={classes.body}
      onClose={onClose}
      title='Share Load via SMS'
      {...props}>
      <div className={classes.row}>
        <Typography
          variant='subtitle1'
          color='textPrimary'
          component='span'
          className='mr4 flex items-center'>
          Share with Driver
        </Typography>
        <ShareLoadTooltip driverId={driverOne?.id} />
      </div>
      <FormProvider {...methods}>
        <NewDriverAutosuggest
          value={driverOne}
          dataTest='input-driver-one'
          onChange={() => {}}
          disabled
        />
        <ShareLoadViaSmsModalForm />
      </FormProvider>
    </ModalBase>
  )
}
